import React from "react";
import { Tooltip } from "@mui/material";
import { Columns2, createLucideIcon } from "lucide-react";
import { useLocation } from "react-router-dom";
import { paneToolbarInkClass } from "../FileViewer/paneToolbar";
import {
  useArtifactViewerMode,
  useViewerMode,
  routePresentsOverlayOnly,
  viewerSurfaceForPath,
} from "./viewerMode";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import type { PaneAction } from "../FileViewer/ArtifactPaneHeader";

// The overlay as a glyph (#12481, Gerry): a bigger box laid over a smaller
// one behind it, the way the drawer lays over the page. Lucide draws the
// opposite (a small box over a big one, picture-in-picture), so this is
// built with its own factory: the same stroke, size and props as its icons.
// The back box is drawn only where the front box does not cover it, and
// sits toward the middle rather than pinned to the top (Gerry: "the top
// little one should be more centered vertically").
const OverlayView = createLucideIcon("OverlayView", [
  [
    "path",
    {
      d: "M8 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1",
      key: "back",
    },
  ],
  [
    "rect",
    { x: "8", y: "9", width: "13", height: "12", rx: "2", key: "front" },
  ],
]);

/**
 * Switches the current surface between the overlay drawer and the split pane,
 * and remembers the choice for that surface (#9746).
 *
 * Lives in the viewer header, next to Close / Download / Details, because the
 * moment a user wants the other layout is the moment they are looking at the
 * one they didn't want.
 */
// The pane toolbar's ink rather than a hand-copied grey: this button sits in
// the same row as Download, Copy link and Close, and had drifted onto
// `text-gray-600` (#4B5563), a visible step darker than its neighbours'
// #6E7480 (#11852). The geometry stays local because the non-compact size has
// no equivalent in the shared button class.
const BUTTON_BASE_CLASS = `${paneToolbarInkClass} cursor-pointer rounded-[8px] transition-all duration-200 flex flex-none items-center justify-center`;

// `compact` matches the unified h-12 pane header (#11548 r51); the default
// matches the roomier h-10 button rows (RMS entity preview). `artifact`
// switches to the artifact-scoped mode (its own memory, split by default —
// Gerry, 2026-09-01), which applies on every route, the documents page
// included.
/**
 * The toggle as a header action (#12091): the artifact panes hand it to
 * their shared header, which folds it into a menu when narrow. Null where
 * the toggle stands down.
 */
export const useViewerModeAction = (artifact: boolean): PaneAction | null => {
  const { pathname } = useLocation();
  const [surfaceMode, setSurfaceMode] = useViewerMode();
  const [artifactMode, setArtifactMode] = useArtifactViewerMode();
  const mode = artifact ? artifactMode : surfaceMode;
  const setMode = artifact ? setArtifactMode : setSurfaceMode;
  const phone = useIsPhoneViewport();

  // At phone both modes render the one full-screen presentation (#11742
  // round 5 T1/T8), so the toggle would offer a choice that changes
  // nothing — it stands down. The stored preference (surface or artifact)
  // is never written from a phone session.
  if (phone) {
    return null;
  }
  // A route that presents only the overlay (a dashboard page, #13602) has no
  // other layout to offer.
  if (routePresentsOverlayOnly(pathname)) {
    return null;
  }
  // The documents page is an overlay-only surface for plain documents: its
  // left pane is the file list the user just picked from, so there is
  // nothing to sit beside. Artifact panes keep their own toggle there.
  if (!artifact && viewerSurfaceForPath(pathname) === "documents") {
    return null;
  }

  // The button offers the layout you don't have, and both its icon and its
  // label name that destination.
  //
  // The icons depict the two layouts rather than a size change: a rectangle
  // split down the middle for side-by-side, and a bigger box laid over a
  // smaller one for the overlay (#12481, Gerry: the earlier plain rectangle
  // "gave the sense of a full screen view"; then "two boxes, one smaller
  // than a bigger that sort of lays over it", the bigger in front). That is
  // the convention Figma, VS Code and Slack use for a layout choice, and it
  // avoids the earlier arrows-out glyph, which read as "make this bigger"
  // and collided in meaning with the entity preview's "Open full page". The wording changed for the same
  // reason: the overlay is a resizable 30-95% drawer, so the old "Expand to
  // full width" promised something the drawer never does.
  // Labelled as the layout itself rather than as an instruction: paired with
  // an icon that draws that layout, "Overlay view" reads as the thing you get,
  // and it stays accurate however the control is reached.
  const offersOverlay = mode === "split";
  return {
    key: "viewer-mode",
    label: offersOverlay ? "Overlay view" : "Side-by-side view",
    icon: offersOverlay ? OverlayView : Columns2,
    onSelect: () => setMode(offersOverlay ? "overlay" : "split"),
    // About the pane, not the artifact: after the ⋯ menu, beside Close
    // (#12481).
    trailing: true,
  };
};

const actionButton = (
  action: PaneAction,
  compact: boolean
): React.ReactElement => {
  const Icon = action.icon;
  return (
    <Tooltip title={action.label} arrow placement="bottom">
      <button
        aria-label={action.label}
        aria-pressed={action.active}
        onClick={(event) => action.onSelect(event.currentTarget)}
        className={`${BUTTON_BASE_CLASS} ${compact ? "h-8 w-8" : "h-10 w-10"}`}
      >
        <Icon className={compact ? "w-4 h-4" : "w-5 h-5"} />
      </button>
    </Tooltip>
  );
};

const ViewerModeToggle = ({
  compact = false,
  artifact = false,
}: {
  compact?: boolean;
  artifact?: boolean;
}) => {
  const action = useViewerModeAction(artifact);
  return action ? actionButton(action, compact) : null;
};

export default ViewerModeToggle;
